import { Button } from "@material-ui/core";
import * as React from "react";
import ReactTooltip from "react-tooltip";
import { eventFromParent } from "@clockwise/client-commons/src/util/event";
import { createUUID } from "@clockwise/client-commons/src/util/uuid";
import { logger } from "@util/logger.util";
import { track, TrackingEvents } from "@util/analytics.util";
import {
  makeEventReschedulerViewActive,
  updateSchedulingSessionId,
} from "@actions/feed-scheduler.actions";
import { EventSchedulingQueryEventParent } from "@components/event-scheduling/EventScheduling";
import classNames from "classnames";
import { IEvent } from "@schema";
import { useDispatch } from "react-redux";
import { useEffect } from "react";

const toolTipId = "tooltip_for_rescheduler_reason";

export type RescheduleButtonProps = {
  enableRescheduling: boolean;
  disableReschedulingReason: JSX.Element | null;
  eventParent: Pick<EventSchedulingQueryEventParent, "events">;
  calendarId: string;
  buttonText?: string;
};

const RescheduleButton = ({
  enableRescheduling,
  disableReschedulingReason,
  eventParent,
  calendarId,
  buttonText,
}: RescheduleButtonProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    track(TrackingEvents.EVENT.SCHEDULING.RESCHEDULE_BUTTON_DISPLAYED, {
      buttonText,
    });
  }, [buttonText]);

  const onClickReschedule = () => {
    const event = eventFromParent(eventParent, calendarId);
    if (!event) {
      logger.error("Unable to find event to reschedule");
      return;
    }

    const schedulingSessionId = createUUID();
    dispatch(updateSchedulingSessionId(schedulingSessionId));

    track(TrackingEvents.SCHEDULING.RESCHEDULER_STARTED, {
      schedulingSessionId,
      externalEventId: event.eventKey.externalEventId,
    });

    dispatch(makeEventReschedulerViewActive(event as IEvent));
  };

  return (
    <div className="cw-mt-4" data-for={toolTipId} data-tip>
      <Button
        onClick={onClickReschedule}
        variant="outlined"
        fullWidth
        disabled={!enableRescheduling}
        cw-id="reschedule-button"
        className={classNames(
          "cw-normal-case cw-font-body cw-text-sm cw-border-cloudly-blue cw-border cw-border-solid",
          {
            "cw-text-slate-standard": enableRescheduling,
          },
        )}
      >
        {buttonText || "Reschedule"}
      </Button>
      <ReactTooltip multiline id={toolTipId} effect="solid" disable={!disableReschedulingReason}>
        {disableReschedulingReason}
      </ReactTooltip>
    </div>
  );
};

export default RescheduleButton;
